import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';

export enum UserPreferenceKey {
  HIDE_COMMUNICATIONS_AUTO_DELETE_INFO = 'HIDE_COMMUNICATIONS_AUTO_DELETE_INFO',
  RELEASE_NOTES_READ_AT = 'RELEASE_NOTES_READ_AT',
  DASHBOARD_PREFERENCES = 'DASHBOARD_PREFERENCES',
  DASHBOARD_LAYOUT = 'DASHBOARD_LAYOUT',
  HIDE_DASHBOARD_UPCOMING_WIDGETS_INFO = 'HIDE_DASHBOARD_UPCOMING_WIDGETS_INFO',
  LEASE_CONTRACT_PANE = 'LEASE_CONTRACT_PANE',
  SEEN_PRODUCT_TOURS = 'SEEN_PRODUCT_TOURS',
  SAVED_FILTERS = 'SAVED_FILTERS',
}

export enum ProductTourId {
  DASHBOARD = 'dashboard',
}

export const USER_PREFERENCE_KEYS: UserPreferenceKey[] = Object.values(UserPreferenceKey);

export const INFO_MESSAGE_PREFERENCE_KEYS: UserPreferenceKey[] = [
  UserPreferenceKey.HIDE_COMMUNICATIONS_AUTO_DELETE_INFO,
  UserPreferenceKey.HIDE_DASHBOARD_UPCOMING_WIDGETS_INFO,
  UserPreferenceKey.SEEN_PRODUCT_TOURS,
];

export interface UserPreferenceEntry {
  name: string;
  value: string | null;
}

const ENABLED_VALUE = 'true';

const SEEN_PRODUCT_TOURS_SEPARATOR = ',';

export const isUserPreferenceKey = (candidate: string): candidate is UserPreferenceKey =>
  USER_PREFERENCE_KEYS.some((key) => key === candidate);

export const getUserPreferenceValue = (key: UserPreferenceKey, preferences?: UserPreferenceEntry[]) =>
  preferences?.find((preference) => preference.name === key)?.value ?? null;

export const isUserPreferenceEnabled = (key: UserPreferenceKey, preferences?: UserPreferenceEntry[]) =>
  getUserPreferenceValue(key, preferences) === ENABLED_VALUE;

export const getSeenProductTours = (preferences?: UserPreferenceEntry[]): string[] =>
  (getUserPreferenceValue(UserPreferenceKey.SEEN_PRODUCT_TOURS, preferences) ?? '')
    .split(SEEN_PRODUCT_TOURS_SEPARATOR)
    .filter((tourId) => !isEmpty(tourId));

export const isProductTourSeen = (tourId: ProductTourId, preferences?: UserPreferenceEntry[]) =>
  getSeenProductTours(preferences).includes(tourId);

export const serializeSeenProductTours = (tourId: ProductTourId, preferences?: UserPreferenceEntry[]) =>
  uniq([...getSeenProductTours(preferences), tourId]).join(SEEN_PRODUCT_TOURS_SEPARATOR);

export const getDismissedInfoMessageKeys = (preferences?: UserPreferenceEntry[]) =>
  INFO_MESSAGE_PREFERENCE_KEYS.filter((key) => !isEmpty(getUserPreferenceValue(key, preferences)));

export const upsertUserPreferenceEntry = (
  entries: UserPreferenceEntry[],
  entry: UserPreferenceEntry
): UserPreferenceEntry[] => [...entries.filter((known) => known.name !== entry.name), entry];

export const serializeUserPreferenceValue = (enabled: boolean) => String(enabled);
